import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { GetFiscalYearsResponse, LocationsClient, PlansClient } from 'src/api/services/generated/api-clients';
import {
  DokaSearchField,
  DokaTable,
  DokaThead,
  DokaTr,
  DokaTh,
  DokaTbody,
  DokaTd,
  DokaButton,
  DokaModalController,
  DokaSelect,
  DokaSelectOption,
} from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { EmptyStateComponent } from 'src/app/shared/components/empty-state/empty-state.component';
import { CreateFiscalYearSlideOverComponent } from './create-fiscal-year-slide-over/create-fiscal-year-slide-over.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { CreateFiscalYearDialogComponent } from 'src/app/shared/components/dashboard/dialogs/create-fiscal-year-dialog/create-fiscal-year-dialog.component';
import { of } from 'rxjs';

@Component({
  templateUrl: './master-data-plans.component.html',
  imports: [
    DokaSearchField,
    I18NextEagerPipe,
    SpinnerComponent,
    DokaTable,
    DokaThead,
    DokaTr,
    DokaTh,
    DokaTbody,
    DokaTd,
    DokaButton,
    EmptyStateComponent,
    DokaSelect,
    DokaSelectOption,
  ],
})
export class MasterDataPlanComponent {
  private readonly plansClient = inject(PlansClient);
  private readonly locationsClient = inject(LocationsClient);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly i18n = inject(I18NextService);
  private readonly toastService = inject(ToastService);

  protected searchText = signal('');
  protected readonly maxVisibleCountries = 5;

  protected readonly selectedDivisionId = signal<string | undefined>(undefined);

  protected readonly divisions = rxResource({
    stream: () => this.locationsClient.getDivisions(),
  });

  protected readonly effectiveDivisionId = computed(() => {
    return this.selectedDivisionId() ?? this.divisions.value()?.data?.[0]?.id;
  });

  protected readonly fiscalYears = rxResource({
    params: () => ({
      divisionId: this.effectiveDivisionId(),
      divisionsLoaded: !this.divisions.isLoading(),
    }),
    stream: ({ params }) => {
      if (!params.divisionsLoaded) {
        const response = new GetFiscalYearsResponse();
        response.data = [];
        return of(response);
      }

      return this.plansClient.getFiscalYears(params.divisionId);
    },
  });

  protected readonly filteredFiscalYears = computed(() => {
    const search = this.searchText().toLowerCase();
    const years = this.fiscalYears.value()?.data ?? [];
    return years.filter(
      (y) =>
        y.year.toString().includes(search)
        || y.plannedCountryNames.some((countryName) => countryName.toLowerCase().includes(search)),
    );
  });

  protected readonly latestFiscalYear = computed(() => {
    const years = this.filteredFiscalYears();
    return years.length > 0 ? Math.max(...years.map((y) => y.year)) : null;
  });

  protected async createNewFiscalYear() {
    const modal = await this.modalCtrl.create(
      {
        component: CreateFiscalYearSlideOverComponent,
        componentProps: {
          divisionId: this.effectiveDivisionId,
          latestYear: signal(this.latestFiscalYear()).asReadonly(),
        },
      },
      'slide-over',
    );

    await modal.present();

    const { data, role } = await modal.onDidDismiss();
    if (role === 'created' && data === true) {
      this.fiscalYears.reload();
      this.toastService.showSuccess(this.i18n.t('MasterData.CreateFiscalYear.FiscalYearCreated'));
    }
  }

  protected async applyFiscalYearToAllCountries(fiscalYearId: string) {
    const fiscalYear = this.filteredFiscalYears().find((fy) => fy.id === fiscalYearId);
    if (!fiscalYear) {
      await this.toastService.showError(this.i18n.t('Dashboard.FiscalYearDialog.CreateError'));
      return;
    }

    const modal = await this.modalCtrl.create({
      component: CreateFiscalYearDialogComponent,
      componentProps: {
        divisionId: this.effectiveDivisionId,
        isYearReadonly: signal(true).asReadonly(),
        latestYear: signal(fiscalYear.year).asReadonly(),
        titleKey: signal('MasterData.PlansGrid.ApplyToAllCountriesTitle').asReadonly(),
        subtitleKey: signal('MasterData.PlansGrid.ApplyToAllCountriesSubtitle').asReadonly(),
      },
    });

    await modal.present();

    const { role } = await modal.onWillDismiss();

    if (role === 'created') {
      this.toastService.showSuccess(this.i18n.t('MasterData.PlansGrid.ApplyToAllCountriesSuccess'));
      this.fiscalYears.reload();
    }
  }

  protected onDivisionChange(divisionId: string) {
    this.selectedDivisionId.set(divisionId);
  }
}
